import '@pine/ui/styles.css'
import { mountApp } from '@pine/ui'
import { PineClient, iceSource } from '@pine/core'
import { createTransport } from '@pine/transport/browser'

/**
 * Web entry.
 *
 * A browser cannot open UDP sockets, so it reaches the DHT through a
 * WebSocket relay. Everything else — identity, room state, media, calls —
 * happens in the tab; the relay only forwards Noise-encrypted frames it has
 * no key for.
 */
const RELAYS = (import.meta.env.VITE_PINE_RELAYS || 'wss://papi.olshub.xyz/relay')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean)

const TURN_HOST = import.meta.env.VITE_PINE_TURN_HOST || 'papi.olshub.xyz'

// Where usernames are reserved. Empty disables reservation entirely and the
// app falls back to resolving handles over the DHT alone.
const REGISTRY = import.meta.env.VITE_PINE_REGISTRY === ''
  ? ''
  : (import.meta.env.VITE_PINE_REGISTRY || 'https://papi.olshub.xyz')

// Where the deployment's own HTTP endpoints live. Same origin for a browser
// tab; a real origin for the Android shell, which runs at pine.localhost and
// has no server of its own behind it.
const API_BASE = (REGISTRY || '').replace(/\/+$/, '')

async function main () {
  const element = document.getElementById('app')

  try {
    const transport = createTransport({ relays: RELAYS, name: 'pine' })
    const client = new PineClient({ transport, registryUrl: REGISTRY })
    // Named before ready(), so the first directory publish already carries it.
    client.platform = native() ? 'android' : 'web'
    client.deviceName = native() ? 'Pine на Android' : 'Pine в браузере'
    await client.ready()

    // Bring rooms online in the background so unread counts are live.
    client.syncAll().catch(() => {})

    const hooks = platform()
    const store = mountApp(element, {
      client,
      // Asked before every call, not once at start: see pine-core's ice.js.
      platform: { ...hooks, iceServers: iceSource({ url: API_BASE, host: TURN_HOST }) }
    })

    if (hooks.notifications) {
      hooks.notifications.attach(client)
      hooks.notifications.refresh().catch(() => {})
    }

    /*
     * The shell's handle on the app.
     *
     * `store` and `client` are for debugging; answerCall and declineCall are
     * load-bearing — they are what the two buttons on the Android call
     * notification reach, from a broadcast receiver that may be running with
     * no activity on screen at all.
     */
    window.pine = {
      client,
      store,
      // From the phone's own call screen, which may have rung on the push
      // before the call itself got here: then it is held until it does.
      answerCall: () => { if (store.calls) store.calls.answerSoon() },
      declineCall: () => { if (store.calls) store.calls.declineSoon() },
      // Hung up from outside the app — a headset, a car, a watch — which
      // reaches the shell through Telecom and the page through here.
      hangUpCall: () => { if (store.calls) store.calls.hangUp() },
      // The phone was unlocked at the call surface's request.
      unlocked: () => { window.dispatchEvent(new CustomEvent('pine:unlocked')) },
      // Firebase handed this device a new push token (see PineMessagingService).
      pushToken: (token) => { client.setPushToken(token).catch(() => {}) },

      // The Android shell calls this when the system file picker refuses to
      // open. Without it the composer waits forever for a file that is never
      // coming, and the attach button looks simply dead.
      pickerFailed: (reason) => {
        window.dispatchEvent(new CustomEvent('pine:picker-failed', { detail: reason || '' }))
      },

      // Pushed by the shell when a headset connects or the call moves to
      // another output, and when the ringtone picker comes back.
      audioRoutes: (routes) => {
        window.dispatchEvent(new CustomEvent('pine:audio-routes', { detail: routes || null }))
      },
      ringtoneChanged: (choice) => {
        window.dispatchEvent(new CustomEvent('pine:ringtone', { detail: choice || null }))
      },

      // The shell saw the phone's network change: Wi-Fi to mobile data, a VPN
      // up or down. The link to the relay went with the old network.
      networkChanged: () => {
        client.reconnect()
        // A call in progress loses its media path the same way; it finds a
        // new one once the room is reachable again (Call.heal).
        if (store.calls && store.calls.active) store.calls.active.networkChanged()
      },
      // From an alarm that wakes a sleeping phone (KeepAlive.kt): is the link
      // still there, and can anybody still find us? A phone asleep runs no
      // timers, so without this a link that died in its sleep stayed dead,
      // and announcements that ran out stayed gone, until somebody picked
      // the phone up.
      keepAlive: () => {
        client.refreshPresence().catch(() => {})
        if (store.calls && store.calls.prefetchIce) store.calls.prefetchIce()
      },
      // A push reached the app while it was still running
      // (PineMessagingService): somebody is calling or wrote. Same as the
      // alarm, at once; the caller is looking for us right now. The TURN
      // credentials a call needs are fetched meanwhile, not after the ring.
      woken: () => {
        client.refreshPresence().catch(() => {})
        if (store.calls && store.calls.prefetchIce) store.calls.prefetchIce()
      }
    }

    // Who may call, by name, for the phone to say before the app is even
    // running (PushRing.kt rings on the push alone). Names from this
    // person's own chat list and contacts; nothing else.
    if (native() && native().setCallerNames) {
      let timer = null
      const send = async () => {
        timer = null
        const names = {}
        for (const contact of await client.listContacts().catch(() => [])) {
          if (contact && contact.account && contact.name) names[contact.account] = contact.name
        }
        for (const record of client.listRooms()) {
          if (record && record.peer && record.name && !record.deleted) names[record.peer] = record.name
        }
        try { native().setCallerNames(JSON.stringify(names)) } catch {}
      }
      const soon = () => { if (!timer) timer = setTimeout(send, 3000) }
      client.on('rooms-changed', soon)
      client.on('contacts-changed', soon)
      send()
    }

    // The link's ups and downs, into the shell's background journal
    // (Journal.kt): "the phone was offline" or not is the first question.
    if (native() && native().journal && client.transport && client.transport.onStatus) {
      client.transport.onStatus((status) => { try { native().journal('link ' + status) } catch {} })
    }

    // Where this phone can be woken when the app is not running — registered
    // on every start, because a registration is what keeps it from expiring.
    if (native() && native().pushToken) {
      const token = native().pushToken()
      if (token) client.setPushToken(token).catch(() => {})
    }

    if (native()) native().ready()
    else {
      registerServiceWorker()
      watchInstallPrompt()
    }
  } catch (error) {
    renderFatal(element, error)
  }
}

/** The Android shell injects this; in a browser it is simply absent. */
function native () {
  return typeof window !== 'undefined' ? window.PineAndroid : undefined
}

/**
 * Platform hooks.
 *
 * The same bundle is loaded by a browser tab and by the Android app's
 * WebView. Where the shell can do better than the web platform — system
 * notifications that outlive the page, the share sheet, a foreground sync
 * service — it is used; otherwise the web equivalents are.
 */
function platform () {
  const bridge = native()

  if (bridge) {
    return {
      kind: 'android',
      // The shell plays the ringtone itself (CallRinger), so the page must not.
      ringsNatively: true,
      notify ({ title, body, roomId }) {
        bridge.notify(title, body, roomId || '')
      },
      setBadge () { /* Android shows counts on the notification itself */ },
      openExternal: (url) => bridge.openExternal(url),
      share: (text) => bridge.share(text),
      // A line for the background journal the shell keeps (Journal.kt).
      journal: typeof bridge.journal === 'function' ? (line) => bridge.journal(String(line)) : null,

      // A call has to be answerable by someone who is not already looking at
      // the app: the shell rings the device's own ringtone and puts the call
      // on the lock screen. Guarded, because a phone still running an older
      // shell has neither method.
      ringCall ({ title, roomId, video }) {
        if (bridge.incomingCall) bridge.incomingCall(title || '', roomId || '', !!video)
      },
      stopRinging () {
        if (bridge.endCall) bridge.endCall()
      },

      /*
       * The phone's own call system. Each call is reported to Telecom so the
       * system treats it as a call — see CallTelecom.kt — and Telecom has to
       * be told when it goes live and when it is over. All three are no-ops
       * on a shell from before 0.1.14.
       */
      telecom: {
        available: typeof bridge.callEnded === 'function',
        placed ({ title, roomId, video }) {
          if (bridge.callPlaced) bridge.callPlaced(title || '', roomId || '', !!video)
        },
        active () { if (bridge.callActive) bridge.callActive() },
        ended (cause) { if (bridge.callEnded) bridge.callEnded(String(cause || 'local')) },
        status () {
          if (!bridge.telecomStatus) return null
          try { return JSON.parse(bridge.telecomStatus()) } catch { return null }
        },
        setEnabled (on) { if (bridge.setTelecomEnabled) bridge.setTelecomEnabled(!!on) }
      },

      // Push wake-ups: whether this build has them, and this device's token.
      push: typeof bridge.pushSupported === 'function' && bridge.pushSupported()
        ? { token: () => (bridge.pushToken ? bridge.pushToken() : '') }
        : null,

      // A call answered on a locked phone is shown over the lock screen; the
      // rest of the app is not, and asks for an unlock before it appears.
      lockScreen: typeof bridge.isLocked === 'function'
        ? {
            locked: () => { try { return !!bridge.isLocked() } catch { return false } },
            unlock: () => { if (bridge.requestUnlock) bridge.requestUnlock() }
          }
        : null,

      /*
       * Where a call comes out of.
       *
       * The web platform has no say in this — output routing belongs to the
       * app — so a WebView plays a call through the loudspeaker like any other
       * media. `available` is what the UI checks before offering the
       * speakerphone button: an older shell has none of these methods, and a
       * button that cannot do anything is worse than no button.
       */
      callAudio: {
        available: typeof bridge.startCallAudio === 'function',
        start (speaker) {
          if (bridge.startCallAudio) bridge.startCallAudio(!!speaker)
        },
        setSpeaker (on) {
          if (bridge.setSpeaker) bridge.setSpeaker(!!on)
        },
        // Earpiece, loudspeaker, wired headset, Bluetooth — or 'auto', which
        // follows whatever is plugged in or connected.
        setRoute (route) {
          if (bridge.setCallRoute) bridge.setCallRoute(String(route))
        },
        routes () {
          if (!bridge.callRoutes) return null
          try { return JSON.parse(bridge.callRoutes()) } catch { return null }
        },
        stop () {
          if (bridge.stopCallAudio) bridge.stopCallAudio()
        }
      },

      /*
       * The ringtone is a phone setting, not a web one: the sound has to play
       * while the page may not even be on screen, and the list to choose from
       * belongs to the system.
       */
      ringtone: {
        available: typeof bridge.ringtone === 'function',
        get: () => (bridge.ringtone ? bridge.ringtone() : ''),
        name: () => (bridge.ringtoneName ? bridge.ringtoneName() : ''),
        set: (value) => { if (bridge.setRingtone) bridge.setRingtone(String(value)) },
        pick: () => { if (bridge.pickRingtone) bridge.pickRingtone() },
        stopPreview: () => { if (bridge.stopRingtonePreview) bridge.stopRingtonePreview() }
      },

      /*
       * Whether this phone will let a call through at all: notifications,
       * battery optimisation, and — on Android 14 and later — permission for
       * a call to take over the screen.
       */
      background: {
        available: typeof bridge.backgroundStatus === 'function',
        status () {
          if (!bridge.backgroundStatus) return null
          try { return JSON.parse(bridge.backgroundStatus()) } catch { return null }
        },
        request: (what) => { if (bridge.requestBackground) bridge.requestBackground(String(what)) },
        // Send that journal to the developer, through the share sheet.
        shareJournal: typeof bridge.shareJournal === 'function' ? () => bridge.shareJournal() : null
      },
      onDeepLink: (handler) => {
        const listener = (event) => handler(event.detail)
        window.addEventListener('pine:deep-link', listener)
        return () => window.removeEventListener('pine:deep-link', listener)
      }
    }
  }

  return {
    kind: 'web',

    /*
     * Notifications in a browser.
     *
     * Asked for only when somebody presses a button — browsers ignore, or
     * quietly block, a permission prompt that arrives by itself — and then
     * wired to Web Push, so a tab that is closed still hears about a call or
     * a message (see server/relay/lib/webpush.js).
     */
    notifications: webNotifications(),

    /*
     * A call arriving while this tab is in the background: nobody is looking
     * at the banner, so the browser shows one of its own. The ringtone plays
     * in the page either way — see tones.js.
     */
    ringCall ({ title, video }) {
      if (document.visibilityState === 'visible') return
      showWebNotification(title || 'Pine', {
        body: video ? 'Входящий видеозвонок' : 'Входящий звонок',
        tag: 'pine-ring',
        requireInteraction: true,
        renotify: true,
        vibrate: [400, 200, 400, 200, 400]
      })
    },
    stopRinging () {
      closeWebNotifications('pine-ring')
    },

    onDeepLink: (handler) => {
      const listener = (event) => {
        if (event.data && event.data.type === 'pine:open-room') handler(event.data.roomId)
      }
      navigator.serviceWorker?.addEventListener('message', listener)
      return () => navigator.serviceWorker?.removeEventListener('message', listener)
    }
  }
}

/**
 * TURN credentials are short-lived and issued by the deployment, so they are
 * fetched rather than baked into the bundle. Falling back to STUN-only still
 * connects on most networks.
 */
/**
 * Register the service worker — now, if the page has already loaded.
 *
 * This used to wait for window 'load', but it is called at the end of main(),
 * after the client has started, and by then 'load' had almost always fired
 * already. The listener never ran, and the worker was never registered: no
 * offline shell, no install to the home screen, and no push. Nothing said so,
 * because nothing depended on it loudly.
 */
function registerServiceWorker () {
  if (!('serviceWorker' in navigator)) return
  const register = () => navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {})
  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register, { once: true })
}

/**
 * Chrome fires beforeinstallprompt when the page qualifies for a WebAPK. We
 * hold the event so the install page can trigger it on a real user gesture.
 */
function watchInstallPrompt () {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    window.__pineInstallPrompt = event
    window.dispatchEvent(new CustomEvent('pine:installable'))
  })
  window.addEventListener('appinstalled', () => {
    window.__pineInstallPrompt = null
    window.dispatchEvent(new CustomEvent('pine:installed'))
  })
}

function renderFatal (element, error) {
  element.innerHTML = ''
  const wrap = document.createElement('div')
  wrap.style.cssText = 'height:100%;display:grid;place-items:center;padding:24px;font-family:system-ui,sans-serif'
  wrap.innerHTML = `
    <div style="max-width:400px;text-align:center;color:#5f6a67">
      <h2 style="color:#14181a;margin:0 0 8px">Pine не смог запуститься</h2>
      <p style="margin:0 0 16px"></p>
      <button style="height:40px;padding:0 20px;border:0;border-radius:12px;background:#1e6a47;color:#fff;cursor:pointer">
        Повторить
      </button>
    </div>`
  wrap.querySelector('p').textContent = error && error.message ? error.message : String(error)
  wrap.querySelector('button').onclick = () => location.reload()
  element.appendChild(wrap)
}

/**
 * An invite link opened in a browser, by somebody who may well have the app.
 *
 * An https link cannot open a desktop app by itself: Windows gives it to the
 * browser, always, and Android only hands it to Pine when the domain vouches
 * for the app (assetlinks.json) — and a chat app's in-app browser ignores
 * even that. So the link landed in the web version, which started a whole
 * second Pine with a fresh, empty account in the tab. Before that happens the
 * page now asks: open this in the app, or carry on here.
 *
 * pine://join/<code> is what the desktop app registers and the Android app
 * listens for; on Android an intent: link reaches it more reliably from
 * Chrome and falls back to the download page if it is not installed.
 */
function appLink () {
  const where = location.pathname + location.search
  const collect = where.match(/\/c\/([a-z0-9]{16,64})/i)
  if (collect) return 'collect/' + collect[1]
  const invite = where.match(/(?:\/i\/|[?&]i=)([a-z0-9]+)/i)
  return invite ? 'join/' + invite[1] : ''
}

function installedAsApp () {
  return native() ||
    navigator.standalone === true ||
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches)
}

function offerTheApp (element, path) {
  const android = /Android/i.test(navigator.userAgent || '')
  const target = android
    ? 'intent://' + path + '#Intent;scheme=pine;package=xyz.olshub.pine;S.browser_fallback_url=' +
      encodeURIComponent(location.origin + '/install.html') + ';end'
    : 'pine://' + path

  return new Promise((resolve) => {
    element.innerHTML = ''
    const wrap = document.createElement('div')
    wrap.style.cssText = 'min-height:100%;display:grid;place-items:center;padding:24px;font-family:system-ui,sans-serif;background:#0c0f0e;color:#e7ece9'
    wrap.innerHTML = `
      <div style="max-width:380px;width:100%;text-align:center">
        <div style="width:56px;height:56px;margin:0 auto 16px;border-radius:16px;background:#1e6a47;display:grid;place-items:center;font-size:28px">🌲</div>
        <h2 style="margin:0 0 8px;font-size:22px">${path.startsWith('collect/') ? 'Коллекционное имя в Pine' : 'Приглашение в Pine'}</h2>
        <p style="margin:0 0 20px;color:#9aa8a2;line-height:1.45">Если Pine установлен, откройте ссылку в приложении — там ваш аккаунт и все чаты.</p>
        <a data-open style="display:block;height:46px;line-height:46px;border-radius:14px;background:#1e6a47;color:#fff;text-decoration:none;font-weight:600;margin-bottom:10px">Открыть в приложении</a>
        <button data-web style="width:100%;height:46px;border-radius:14px;border:1px solid #3a4540;background:transparent;color:#e7ece9;font:inherit;cursor:pointer">Продолжить в браузере</button>
        <p style="margin:18px 0 0;font-size:13px;color:#7d8c86">Нет приложения? <a href="/install.html" style="color:#4bb07a">Скачать Pine</a></p>
      </div>`
    wrap.querySelector('[data-open]').setAttribute('href', target)
    wrap.querySelector('[data-web]').onclick = () => resolve()
    element.appendChild(wrap)
  })
}

async function start () {
  const path = appLink()
  if (path && !installedAsApp()) {
    await offerTheApp(document.getElementById('app'), path)
    document.getElementById('app').innerHTML = ''
  }
  return main()
}

start()

/* ------------------------------------------------------------------ *
 * Web notifications and Web Push
 * ------------------------------------------------------------------ */

async function showWebNotification (title, options) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  try {
    const registration = await navigator.serviceWorker?.getRegistration()
    if (registration) {
      await registration.showNotification(title, {
        icon: '/icons/icon-192.png',
        badge: '/icons/badge.png',
        ...options
      })
    }
  } catch { /* a notification that cannot be shown is not worth an error */ }
}

async function closeWebNotifications (tag) {
  try {
    const registration = await navigator.serviceWorker?.getRegistration()
    const open = registration ? await registration.getNotifications({ tag }) : []
    for (const notification of open) notification.close()
  } catch { /* nothing to close */ }
}

function base64UrlToBytes (value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((value.length + 3) % 4)
  const raw = atob(padded)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

/**
 * Subscribe this browser to push and hand the subscription to the client,
 * which registers it (signed) with the server.
 *
 * The server's public VAPID key is asked for rather than built in, so a
 * deployment with its own key works without a rebuild. A subscription made
 * against a different key is dropped and made again — that only happens if
 * the server's key was ever replaced.
 */
/*
 * The server's public push key, asked for once, ahead of time.
 *
 * Safari only lets a page subscribe to push close behind the tap that allowed
 * notifications. Fetching the key between the two — a network round trip —
 * could use that moment up on a slow connection, and the subscription then
 * failed with nothing on screen to say so. With the key already here, the
 * subscription follows the permission immediately.
 */
let pushStatus = null

function loadPushStatus (client) {
  if (!pushStatus) {
    const base = (client.registryUrl || '').replace(/\/+$/, '')
    pushStatus = fetch(base + '/api/push/status').then((r) => r.json()).catch(() => {
      pushStatus = null
      return null
    })
  }
  return pushStatus
}

async function subscribeWebPush (client) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false

  const status = await loadPushStatus(client)
  if (!status || !status.web) return false

  const registration = await navigator.serviceWorker.ready
  const key = base64UrlToBytes(status.web)
  let subscription = await registration.pushManager.getSubscription()

  if (subscription) {
    const current = subscription.options && subscription.options.applicationServerKey
    const same = current && new Uint8Array(current).every((b, i) => b === key[i])
    if (!same) {
      await subscription.unsubscribe().catch(() => {})
      subscription = null
    }
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  }

  await client.setPushToken(JSON.stringify(subscription), 'web')
  return true
}

/**
 * An iPhone or iPad showing Pine in a Safari tab rather than as an app.
 *
 * iOS gives web push only to a site added to the Home Screen and opened from
 * there; in a tab there is no Notification object at all. The app used to
 * treat that as "unsupported" and say nothing, so an iPhone that never got a
 * notification also never learnt that it could.
 */
function needsHomeScreen () {
  const ua = navigator.userAgent || ''
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  if (!ios) return false
  const standalone = navigator.standalone === true ||
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches)
  return !standalone
}

function webNotifications () {
  let client = null
  return {
    attach (c) {
      client = c
      loadPushStatus(c).catch(() => {})
    },

    /** 'granted' | 'denied' | 'default' | 'unsupported' | 'install' */
    state () {
      if (typeof Notification === 'undefined' || !('PushManager' in window)) {
        return needsHomeScreen() ? 'install' : 'unsupported'
      }
      return Notification.permission
    },

    /** Ask — from a button press — and wire up push if the answer is yes. */
    async enable () {
      if (typeof Notification === 'undefined') return needsHomeScreen() ? 'install' : 'unsupported'
      const answer = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission()
      if (answer === 'granted') {
        // A site allowed to notify is one Chrome will also let keep its
        // storage — which is what stops the browser clearing the account.
        navigator.storage?.persist?.().catch(() => {})
        if (client) await subscribeWebPush(client).catch(() => false)
      }
      return answer
    },

    /** On every start: renew the subscription if permission was given before. */
    async refresh () {
      if (!client || typeof Notification === 'undefined' || Notification.permission !== 'granted') return
      await subscribeWebPush(client).catch(() => false)
    }
  }
}
